/** Freshness guard for the unload-path cached-envelope flush (P128 durability).
 *
 * The flush writes, synchronously and without a worker round-trip, the newest
 * save envelope the main thread already holds. That is only safe when the
 * cached bytes are provably the freshest for BOTH axes:
 * - ordering: two saves can be in flight at once; an older response landing
 *   after a newer one must never overwrite the newer bytes (store OR cache);
 * - generation: load / import / restore-backup / delete / new session replace
 *   what a slot means, so envelopes from before that point are stale.
 * Storage failures must never become a false "saved" claim or break the
 * unload handler chain: flush failures return false.
 */
export class SaveFreshness {
  private seq = 0;
  private gen = 0;
  private readonly latestIssued = new Map<number, number>();
  private readonly cached = new Map<number, { gen: number; seq: number; bytes: string }>();

  /** Call on anything that replaces slot content or the running session. */
  invalidate(): void {
    this.gen += 1;
    this.cached.clear();
    this.latestIssued.clear();
  }

  /** A save request is being issued; returns its sequence number. */
  issue(slot: number): number {
    const seq = ++this.seq;
    this.latestIssued.set(slot, seq);
    return seq;
  }

  /** A save response landed. Returns true when it is still the latest issued
   *  for the slot (and therefore safe to store/cache); stale responses are
   *  dropped, never stored over newer bytes. */
  accept(slot: number, seq: number, bytes: string): boolean {
    if (this.latestIssued.get(slot) !== seq) return false;
    this.cached.set(slot, { gen: this.gen, seq, bytes });
    return true;
  }

  /** Bytes safe to flush synchronously on unload, or null when nothing fresh
   *  enough is held. */
  flushable(slot: number): string | null {
    const c = this.cached.get(slot);
    if (c === undefined) return null;
    if (c.gen !== this.gen) return null;
    if (this.latestIssued.get(slot) !== c.seq) return null;
    return c.bytes;
  }
}

/** Run an unload-path write without ever throwing into the handler chain. */
export function safeFlush(write: () => boolean): boolean {
  try {
    return write();
  } catch {
    return false;
  }
}
